/**
 * Keyword-search request builder — extracted from SearchPage so it can be
 * unit-tested directly (it was a closure inside the component). Behaviour is
 * unchanged; SearchPage now imports these instead of defining them inline.
 */
import type { KeywordSearchGroup, KeywordSearchSource, KeywordSearchRequest, SearchRegion } from '../types';
import type { KeywordFilters } from '../components/KeywordFilterBar';
import {
  regionsToBackendRegion, KW_CATEGORY_MAP, buildCategoriesByRegion,
  DEFAULT_LETTER_CORPUS, DEFAULT_LETTER_DIVISIONS, DEFAULT_LETTER_ROLES,
  DEFAULT_LETTER_TYPES, DEFAULT_MARKETING_CATEGORIES, DEFAULT_REGIONS,
  DEFAULT_SEARCH_MODE,
} from '../constants/filterConstants';
import { keywordDocSourceEnabled } from '../constants/filterConstants';
import type { KeywordDocSource } from '../constants/filterConstants';
import { NATIVE_SOURCES, coerceKeywordRequest } from '../utils/keywordMatchRule';

/** Default keyword filters (fresh form). */
export const baseFilters = (): KeywordFilters => ({
  drug_name: '',
  manufacturer: '',
  pharmacological_class: [],
  regions: DEFAULT_REGIONS,
  marketingCategories: DEFAULT_MARKETING_CATEGORIES,
  searchMode: DEFAULT_SEARCH_MODE,
  letterTypes: DEFAULT_LETTER_TYPES,
  letterCorpus: DEFAULT_LETTER_CORPUS,
  letterRoles: DEFAULT_LETTER_ROLES,
  letterDivisions: DEFAULT_LETTER_DIVISIONS,
});

// One page size for every keyword search. Backend page caps
// must stay compatible with this (keyword_search_service.py: UI_PAGE_DRUGS >=
// it, CANDIDATE_POOL a multiple of it), or pages silently skip drugs.
export const KW_PAGE_SIZE = 200;

// A restored request (URL kwConfig, sessionStorage, Recent Searches replay)
// may carry the page size it was saved with (50, or 10 for exact). Re-derive
// limit/offset from the page number so it matches the pager's KW_PAGE_SIZE —
// otherwise page 2 steps past rows page 1 never showed.
export function withKwPage(request: KeywordSearchRequest, page: number): KeywordSearchRequest {
  const p = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;
  return coerceKeywordRequest({ ...request, limit: KW_PAGE_SIZE, offset: (p - 1) * KW_PAGE_SIZE });
}

/** Identity of a search independent of paging (P10): the persisted server
 *  sort and the passages cache key are scoped to it. */
export function kwSearchIdentity(req: KeywordSearchRequest): string {
  return JSON.stringify({ ...req, limit: undefined, offset: undefined });
}

// A single exact-phrase row drives the results UI's contiguous-phrase
// highlighting (one <mark> instead of per-word marks). The windowed "N+"
// total label is keyed on response.count_is_windowed, not this (spec
// 2026-09-18 R7) — this helper no longer feeds that decision.
export function isSingleExactPhrase(groups: KeywordSearchGroup[]): boolean {
  const rows = groups.flatMap((g) => g.rows).filter((r) => !r.drug_field);
  return rows.length === 1;
}

// Build the keyword-search request body.  Letter mode (sources ===
// ['fda_letter']) and drug mode (label/sba/epar) speak different shapes — the
// backend's ``_validate_letter_keyword_request`` rejects ``groups``, drug-only
// filters, and ``include_generics: false`` in letter mode.  We collapse
// ``groups`` to a flat ``rows`` list and strip the unsupported fields rather
// than send them and get a 400.
export function buildKwRequest(
  kwFilters: KeywordFilters,
  groups: KeywordSearchGroup[],
  sources: KeywordSearchSource[],
  offset: number,
  /** Label VERSION to search. 'origin' = each product's ORIGINAL label.
   *  Admin-only, and the backend enforces that independently — this only
   *  decides what we ASK for, never what we are allowed to get. Omitted
   *  entirely when undefined so the request is byte-identical to before. */
  versionScope?: 'current' | 'origin',
  /** Admin "Search in" (spec 2026-10-16 §6a, admin-only until K4).
   *  ``documentEngine`` sends ``document_engine: true``: document corpora go
   *  to the one engine (drug rows + unlinked group) and ``['fda_letter']`` is
   *  NOT the legacy letter shape. Omitted for non-admins, whose bodies stay
   *  byte-identical. */
  docOpts?: { documentEngine: boolean },
): KeywordSearchRequest {
  const documentEngine = docOpts?.documentEngine === true;
  const isLetterMode = !documentEngine && sources.length === 1 && sources[0] === 'fda_letter';
  if (isLetterMode) {
    const flatRows = groups.flatMap((g) =>
      g.rows.map((r) => ({
        keyword: r.keyword,
        // Carried so the backend refuses a field row (422) instead of
        // searching its value as letter text.
        ...(r.drug_field ? { drug_field: r.drug_field } : {}),
        // Letter mode supports only the narrow row subset the validator
        // allows; force defaults for every per-row field so a stale group
        // built in drug mode can't smuggle in
        // synonyms / section_scope.
        match_type: 'exact' as const,
        section_scope: null,
        use_synonyms: false,
        custom_synonyms: null,
      })),
    );
    return {
      rows: flatRows,
      row_operator: 'AND',
      sources,
      // Letter-corpus filters. Only attached in letter mode — drug-corpus
      // requests below omit them entirely. Snake_case matches the backend
      // Pydantic field names.
      letter_types: kwFilters.letterTypes,
      letter_corpus: kwFilters.letterCorpus,
      letter_roles: kwFilters.letterRoles,
      letter_divisions: kwFilters.letterDivisions,
      limit: KW_PAGE_SIZE,
      offset,
    } as KeywordSearchRequest;
  }
  const kwRegion =
    (regionsToBackendRegion(kwFilters.regions) as KeywordSearchRequest['region'] ?? null);
  const kwCats = kwFilters.marketingCategories
    .map((c) => KW_CATEGORY_MAP[c])
    .filter((v): v is string => Boolean(v));
  // Region-scoped category filter. Each category pill belongs to exactly one
  // region (REGION_CATEGORY_MAP); we group the selected pills' DB statuses under
  // their region so the backend can scope the filter per-corpus instead of
  // ANDing it globally (which used to drop every CA/AU/JP drug the moment "EU
  // Authorised" was on). ``eu_authorised`` and ``uk_authorised`` both map to the
  // DB value "Authorised" — grouping here (not on the flattened list) keeps
  // EU-vs-UK unambiguous. CA/AU/JP have no pills → never appear here → the
  // backend treats them as facetless and passes them through untouched.
  const catsByRegion = buildCategoriesByRegion(kwFilters.regions, kwFilters.marketingCategories);
  return {
    groups,
    drug_name: kwFilters.drug_name || null,
    manufacturer: kwFilters.manufacturer || null,
    pharmacological_class: kwFilters.pharmacological_class.length > 0 ? kwFilters.pharmacological_class : null,
    marketing_category: kwCats.length > 0 ? kwCats : null,
    region: kwRegion,
    // Always send the region-scoped map — even empty ``{}`` ("region-scoped
    // mode, no constraints") — so the backend never falls back to the
    // region-blind flat ``marketing_category`` on stale filter state (e.g. CA
    // selected while old NDA/BLA values linger).
    marketing_categories_by_region: catsByRegion,
    // Pass the full multi-region list so backend doesn't silently drop it when
    // more than one region is selected. Server prefers ``regions`` over
    // ``region`` when both present.
    ...(kwFilters.regions.length > 0 ? { regions: kwFilters.regions } : {}),
    include_generics: kwFilters.marketingCategories.includes('anda'),
    sources,
    // Only emitted when explicitly set, so the default request body is
    // unchanged and the backend's cache key cannot shift for normal users.
    ...(versionScope ? { version_scope: versionScope } : {}),
    ...(documentEngine ? {
      document_engine: true,
      // Enforcement-letter filters ride along only when that corpus is searched.
      ...(sources.includes('fda_letter') ? {
        letter_types: kwFilters.letterTypes,
        letter_corpus: kwFilters.letterCorpus,
        letter_roles: kwFilters.letterRoles,
        letter_divisions: kwFilters.letterDivisions,
      } : {}),
    } : {}),
    limit: KW_PAGE_SIZE,
    offset,
  };
}

// ── Admin "Search in" state <-> kwConfig (plan PD10 / PD11) ──────────────
// Document corpus keys the admin multi-select can put in ``sources`` (K1b, K2b).
const DOC_SOURCE_KEYS: ReadonlySet<string> = new Set(['fda_letter', 'fda_approval_letter']);
// Never sent by a non-admin UI: document corpora not yet offered to anyone
// outside the admin gate.
const ADMIN_ONLY_SOURCES: ReadonlySet<string> = new Set(['sba', 'fda_approval_letter']);
const LABEL_FAMILY_SOURCES: ReadonlySet<string> =
  new Set(['label', 'eu', 'ca_pm', 'au_pi', 'jp', 'jp_ja', 'ie', 'ch', 'de', 'fr', 'ch_src', 'it', 'fda_label_pdf']);

function parseKwConfig(raw: string | null): (KeywordSearchRequest & Record<string, unknown>) | null {
  if (!raw) return null;
  try {
    const cfg = JSON.parse(raw) as unknown;
    return cfg && typeof cfg === 'object' ? cfg as KeywordSearchRequest & Record<string, unknown> : null;
  } catch { return null; }
}

/** The sources an admin search sends: the form's label sources when Labels
 *  is selected, plus each selected document corpus. Native corpora
 *  (NATIVE_SOURCES) and origin scope are searched alone, so they pass through
 *  untouched. */
export function applyKwDocSources(
  formSources: KeywordSearchSource[], docSources: KeywordDocSource[], originScope: boolean,
): KeywordSearchSource[] {
  if (originScope || formSources.some((s) => NATIVE_SOURCES.has(s))) return formSources;
  const out: KeywordSearchSource[] = [
    ...(docSources.includes('labels') ? formSources : []),
    ...docSources.filter((d): d is Exclude<KeywordDocSource, 'labels'> => d !== 'labels'),
  ];
  return out.length > 0 ? out : formSources;
}

/** PD11: the "Search in" selection is DERIVED from the saved request, not a
 *  URL param of its own — its document keys, plus Labels when any
 *  label-family source is present. Falls back to Labels. */
export function deriveKwDocSources(rawKwConfig: string | null): KeywordDocSource[] {
  const sources = (parseKwConfig(rawKwConfig)?.sources ?? []) as string[];
  const out: KeywordDocSource[] = [];
  if (sources.some((s) => LABEL_FAMILY_SOURCES.has(s))) out.push('labels');
  for (const s of sources) {
    if (DOC_SOURCE_KEYS.has(s) && !out.includes(s as KeywordDocSource)) out.push(s as KeywordDocSource);
  }
  return out.length > 0 ? out : ['labels'];
}

/** PD10: a replayed request a non-admin must not run as-is — it asks for
 *  the document engine or names an admin-only document corpus. (A plain
 *  ``['fda_letter']`` without the flag is today's non-admin letter mode.) */
export function kwConfigNeedsAdmin(rawKwConfig: string | null): boolean {
  const cfg = parseKwConfig(rawKwConfig);
  return cfg ? kwRequestNeedsAdmin(cfg) : false;
}

/** ``kwConfigNeedsAdmin`` on a parsed request. */
/** Replay guard: a restored/replayed request may name ``fda_approval_letter``
 *  while the option is ineligible HERE (regions not exactly ['US']). Drop it by the same rule as the "Search in" option
 *  (``keywordDocSourceEnabled``) before dispatch; Labels if nothing is left. */
export function dropIneligibleApprovalLetter(req: KeywordSearchRequest): KeywordSearchRequest {
  const sources = (req.sources ?? []) as string[];
  if (!sources.includes('fda_approval_letter')
      || keywordDocSourceEnabled('fda_approval_letter', (req.regions ?? []) as SearchRegion[])) return req;
  const kept = sources.filter((x) => x !== 'fda_approval_letter');
  return { ...req, sources: (kept.length > 0 ? kept : ['label']) as KeywordSearchSource[] };
}

export function kwRequestNeedsAdmin(req: KeywordSearchRequest): boolean {
  return req.document_engine === true
    || ((req.sources ?? []) as string[]).some((s) => ADMIN_ONLY_SOURCES.has(s));
}

/** ``coerceKwConfigForNonAdmin`` on a parsed request. */
export function sanitizeKwRequestForNonAdmin(req: KeywordSearchRequest): KeywordSearchRequest {
  return JSON.parse(coerceKwConfigForNonAdmin(JSON.stringify(req))) as KeywordSearchRequest;
}

/** PD10: the non-admin rewrite — back to the Labels default (never the
 *  legacy letter mode): document sources, ``document_engine``, the unlinked
 *  paging and the letter filters are removed. */
export function coerceKwConfigForNonAdmin(rawKwConfig: string): string {
  const cfg = parseKwConfig(rawKwConfig);
  if (!cfg) return rawKwConfig;
  const {
    document_engine: _de, unlinked_limit: _ul, unlinked_offset: _uo,
    letter_types: _lt, letter_corpus: _lc, letter_roles: _lr, letter_divisions: _ld,
    ...rest
  } = cfg;
  const sources = ((cfg.sources ?? []) as string[])
    .filter((s) => !DOC_SOURCE_KEYS.has(s) && !ADMIN_ONLY_SOURCES.has(s));
  return JSON.stringify({ ...rest, sources: sources.length > 0 ? sources : ['label'] });
}

// Keyword-search error box message: the backend's `detail` when it's a
// non-empty string (a FastAPI 422's `detail` is an array, not a string, and
// is excluded so it doesn't render as "[object Object]"), else `.message`.
export function formatKeywordSearchError(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } } | undefined)?.response
    ?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return (error as Error | undefined)?.message || 'An error occurred while searching';
}
